/**
 * Notification Service - Feature A5
 * PWA Push Notifications for Reminders
 */

import { Reminder, shouldFireToday } from '../types/reminderTypes';
import type { Note } from '../types/note';

/**
 * Check if browser supports notifications
 */
export const supportsNotifications = (): boolean => {
    return 'Notification' in window;
};

/**
 * Get current notification permission status
 */
export const getNotificationPermission = (): NotificationPermission | 'unsupported' => {
    if (!supportsNotifications()) return 'unsupported';
    return Notification.permission;
};

/**
 * Request notification permission
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission | 'unsupported'> => {
    if (!supportsNotifications()) return 'unsupported';

    try {
        const permission = await Notification.requestPermission();
        return permission;
    } catch (error) {
        console.error('Failed to request notification permission:', error);
        return 'denied';
    }
};

/**
 * Show a notification
 */
export const showNotification = (
    title: string,
    options?: NotificationOptions
): Notification | null => {
    if (!supportsNotifications() || Notification.permission !== 'granted') {
        console.warn('Notification not supported or permission not granted');
        return null;
    }

    try {
        const notification = new Notification(title, {
            icon: '/icon-192.png',
            badge: '/icon-192.png',
            ...options,
        });
        console.log('Notification created:', notification);
        return notification;
    } catch (error) {
        console.error('Failed to show notification:', error);
        return null;
    }
};

/**
 * Show reminder notification for a note
 */
export const showReminderNotification = (
    note: Note,
    daysBefore: number
): Notification | null => {
    const dayText = daysBefore === 1 ? 'Ngày mai' : `Còn ${daysBefore} ngày`;

    return showNotification(
        `🔔 ${note.title || 'Sự kiện'}`,
        {
            body: `${dayText} - ${note.date}`,
            tag: `reminder-${note.id}-${daysBefore}`,
            requireInteraction: true,
            data: {
                noteId: note.id,
                date: note.date,
            },
        }
    );
};

/**
 * Check all notes for pending reminders
 */
export const checkPendingReminders = (
    notes: Note[],
    today: Date = new Date()
): { note: Note; reminder: Reminder }[] => {
    const pending: { note: Note; reminder: Reminder }[] = [];

    for (const note of notes) {
        if (!note.reminders) continue;

        const eventDate = new Date(note.date);

        for (const reminder of note.reminders) {
            if (shouldFireToday(eventDate, reminder, today)) {
                pending.push({ note, reminder });
            }
        }
    }

    return pending;
};

/**
 * Fire all pending reminders
 */
export const firePendingReminders = (
    notes: Note[],
    onReminderFired?: (noteId: string, reminderId: string) => void
): number => {
    const pending = checkPendingReminders(notes);
    let firedCount = 0;

    for (const { note, reminder } of pending) {
        const notification = showReminderNotification(note, reminder.daysBefore);

        if (notification) {
            firedCount++;
            onReminderFired?.(note.id, reminder.id);
        }
    }

    return firedCount;
};

/**
 * Schedule reminder check (runs on app load)
 */
export const scheduleReminderCheck = (
    notes: Note[],
    onReminderFired?: (noteId: string, reminderId: string) => void
): void => {
    // Check immediately on load
    firePendingReminders(notes, onReminderFired);

    // Also check at 9:00 AM if app is open
    const now = new Date();
    const nineAM = new Date(now);
    nineAM.setHours(9, 0, 0, 0);

    if (now < nineAM) {
        const msUntil9AM = nineAM.getTime() - now.getTime();
        setTimeout(() => {
            firePendingReminders(notes, onReminderFired);
        }, msUntil9AM);
    }
};

/**
 * Notification permission status UI helper
 */
export const getPermissionStatusText = (): { text: string; color: string } => {
    const permission = getNotificationPermission();

    switch (permission) {
        case 'granted':
            return { text: '✓ Đã bật thông báo', color: 'text-green-400' };
        case 'denied':
            return { text: '✗ Thông báo bị chặn', color: 'text-red-400' };
        case 'default':
            return { text: 'Chưa bật thông báo', color: 'text-yellow-400' };
        case 'unsupported':
            return { text: 'Trình duyệt không hỗ trợ', color: 'text-gray-400' };
    }
};
